import axios from 'axios';
import classNames from 'classnames';
import React, { useCallback, useContext, useState } from 'react';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useLocation, useNavigate } from 'react-router-dom';

import { AlertContext, trackerState } from '../App';
import { EditMyInfoProps } from '.';
import { Header } from './Header';
import { LabelInput } from './LabelInput';
import { UserInfo } from '.';
import { getErrorMessage } from '../util/message';

import style from './EditMyInfoComponents.module.scss';
import { useTranslation } from 'react-i18next';

interface EditMyInfoPhoneNumberProps {
  accessToken: string;
  userInfo: UserInfo;
  onComplete(phoneNumber: string): void;
  onFailed(context: string): void;
}

const EditMyInfoPhoneNumber: React.FC<EditMyInfoPhoneNumberProps> = (props: EditMyInfoPhoneNumberProps) => {
  const { t } = useTranslation();
  const { accessToken, onComplete, onFailed, userInfo } = props;
  const [ inputValue, setInputValue ] = useState('');
  const [ disabled, setDisable ] = useState(true);
  const config = {
    baseURL: process.env.REACT_APP_BACKEND_URL,
    headers: { Authorization: `Bearer ${accessToken}` },
  };
  const onChangeHandler = (e: React.ChangeEvent<HTMLDataElement>) => {
    const reg = /^\d{10,11}$/;
    const val = e.currentTarget.value.slice(0, 11);
    const valid = !reg.test(val);
    setInputValue(val);
    setDisable(valid);
  };
  const tracker = useContext(trackerState);
  const trackEvent = (category: string, action: string, label: string) => {
    tracker && tracker.trackEvent(category, action, label);
  };
  const handleOnClick = async () => {
    let valid = false;
    const phoneNumber = parsePhoneNumberFromString(inputValue, userInfo.country);

    valid = phoneNumber != null && phoneNumber.isValid();
    setDisable(true);
    trackEvent('휴대폰번호 인증번호', '요청', '휴대폰번호 인증번호 요청 버튼 클릭');

    if (valid && phoneNumber != null) {
      try {
        const data = { phone_number: phoneNumber.number };
        await axios.post(`/mobile/users/${userInfo.uuid}/request-phone-authkey/`, data, config);
        onComplete(phoneNumber.formatNational());
      } catch (err) {
        valid = false;
        onFailed(getErrorMessage(err));
      }
    } else {
      onFailed(t('10-11자리 전화번호를 정확히 입력하세요.'));
    }
    setDisable(valid);
  };
  const handleFocusPhoneNumber = () => {
    trackEvent('휴대폰번호', '입력', '휴대폰번호 입력폼 클릭');
  };

  return (
        <>
            <div className={classNames(style.cards)}>
                <LabelInput
                    type="number"
                    label={t('전화번호')}
                    placeholder="01012345678"
                    value={inputValue}
                    onChange={onChangeHandler}
                    onFocus={handleFocusPhoneNumber}
                />
            </div>

            <div className={style.confirm}>
                <button
                    disabled={disabled}
                    onClick={handleOnClick}
                >
                    {t('인증번호 받기')}
                </button>
            </div>
        </>
  );
};

type EditPhoneNumberProps = EditMyInfoProps;

const EditPhoneNumber: React.FC<EditPhoneNumberProps> = (props: EditPhoneNumberProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { accessToken, userInfo, handleUserChanges } = props;
  const onComplete = (phoneNumber: string) => {
    handleUserChanges && handleUserChanges({ ...userInfo, phoneNumber });
    navigate('/auth', { state: location.state });
  };
  const alert = useContext(AlertContext);
  const handleFailed = useCallback((message: string) => {
    alert({
      content: message,
      type: 'error',
    });
  }, [alert]);

  return (
        <>
            <Header title={t('변경할 본인의 번호를 입력하세요.')} />
            <EditMyInfoPhoneNumber
                accessToken={accessToken}
                userInfo={userInfo}
                onComplete={onComplete}
                onFailed={handleFailed}
            />
        </>
  );
};

export default EditPhoneNumber;
